import Modal from "@/components/Modal";
import SkeletonDashboard from "@/components/SkeletonDashboard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import { useTahun } from "@/hooks/useTahun";
import { getDataRekapPendaftaran } from "@/services/getData";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FaSort, FaUserGraduate, FaUsers } from "react-icons/fa";
import { LuDownload, LuListFilter } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";
import FormFilterPenerimaan from "./FormFilterPenerimaan";

type DataRekapPendaftaran = {
  id_ref_jalur_jenis: number;
  kode_jalur: string;
  nama: string;
  nama_jalur_jenis: string;
  pendaftar_p1: string;
  pendaftar_p2: string;
  pendaftar_p3: string;
  pendaftar_p4: string;
  pendaftar_valid_p1: string | null;
  pendaftar_valid_p2: string | null;
  pendaftar_valid_p3: string | null;
  pendaftar_valid_p4: string | null;
  uk_id: number;
  uk_kode: string;
  fakultas?: string;
  daya_tampung?: number;
};

// --- COMPONENT ---
export default function TableRekapPenerimaan() {
  const { apiBaseUrl } = useApiBaseUrl();
  const [searchParams] = useSearchParams();
  const idsJalurJenisParams = searchParams.get("ids_ref_jalur_jenis") || "";

  const { selectedTahun } = useTahun();
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [sortIndex, setSortIndex] = useState<number | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [sortKeyMode, setSortKeyMode] = useState<
    "valid" | "raw" | "grand-valid" | "grand-raw"
  >("valid");

  const {
    data: dataRekapPendaftaran = [],
    isPending: isPendingDataRekapPendaftaran,
  } = useQuery({
    queryKey: [
      "dataRekapPendaftaran",
      idsJalurJenisParams,
      selectedTahun?.label,
    ],
    queryFn: () =>
      getDataRekapPendaftaran(idsJalurJenisParams, selectedTahun?.label),
  });

  // --- FILTER & SORT LOGIC ---
  const filteredDataRekap =
    searchQuery?.length > 0 &&
    dataRekapPendaftaran?.rekap &&
    dataRekapPendaftaran?.rekap?.length > 0
      ? dataRekapPendaftaran?.rekap?.filter((rekap: DataRekapPendaftaran) => {
          return rekap.nama.toLowerCase().includes(searchQuery.toLowerCase());
        })
      : dataRekapPendaftaran?.rekap;

  const sortedFiltered = useMemo(() => {
    if (
      sortIndex === null &&
      sortKeyMode !== "grand-valid" &&
      sortKeyMode !== "grand-raw"
    ) {
      return filteredDataRekap;
    }

    return [...(filteredDataRekap || [])].sort((a, b) => {
      let valA = 0;
      let valB = 0;

      if (sortKeyMode === "valid") {
        valA =
          Number(
            a[
              `pendaftar_valid_p${sortIndex! + 1}` as keyof DataRekapPendaftaran
            ],
          ) || 0;
        valB =
          Number(
            b[
              `pendaftar_valid_p${sortIndex! + 1}` as keyof DataRekapPendaftaran
            ],
          ) || 0;
      } else if (sortKeyMode === "raw") {
        valA =
          Number(
            a[`pendaftar_p${sortIndex! + 1}` as keyof DataRekapPendaftaran],
          ) || 0;
        valB =
          Number(
            b[`pendaftar_p${sortIndex! + 1}` as keyof DataRekapPendaftaran],
          ) || 0;
      } else if (sortKeyMode === "grand-valid") {
        valA =
          (Number(a.pendaftar_valid_p1) || 0) +
          (Number(a.pendaftar_valid_p2) || 0) +
          (Number(a.pendaftar_valid_p3) || 0) +
          (Number(a.pendaftar_valid_p4) || 0);
        valB =
          (Number(b.pendaftar_valid_p1) || 0) +
          (Number(b.pendaftar_valid_p2) || 0) +
          (Number(b.pendaftar_valid_p3) || 0) +
          (Number(b.pendaftar_valid_p4) || 0);
      } else if (sortKeyMode === "grand-raw") {
        valA =
          (Number(a.pendaftar_p1) || 0) +
          (Number(a.pendaftar_p2) || 0) +
          (Number(a.pendaftar_p3) || 0) +
          (Number(a.pendaftar_p4) || 0);
        valB =
          (Number(b.pendaftar_p1) || 0) +
          (Number(b.pendaftar_p2) || 0) +
          (Number(b.pendaftar_p3) || 0) +
          (Number(b.pendaftar_p4) || 0);
      }

      return sortAsc ? valA - valB : valB - valA;
    });
  }, [filteredDataRekap, sortIndex, sortAsc, sortKeyMode]);

  // --- TOTAL CALCULATION ---
  const totals = {
    totalValidP1: 0,
    totalValidP2: 0,
    totalValidP3: 0,
    totalValidP4: 0,
    totalP1: 0,
    totalP2: 0,
    totalP3: 0,
    totalP4: 0,
    totalDayaTampung: 0,
  };

  sortedFiltered && sortedFiltered?.length > 0
    ? sortedFiltered.forEach((item: DataRekapPendaftaran) => {
        totals.totalValidP1 += Number(item.pendaftar_valid_p1) || 0;
        totals.totalValidP2 += Number(item.pendaftar_valid_p2) || 0;
        totals.totalValidP3 += Number(item.pendaftar_valid_p3) || 0;
        totals.totalValidP4 += Number(item.pendaftar_valid_p4) || 0;
        totals.totalP1 += Number(item.pendaftar_p1) || 0;
        totals.totalP2 += Number(item.pendaftar_p2) || 0;
        totals.totalP3 += Number(item.pendaftar_p3) || 0;
        totals.totalP4 += Number(item.pendaftar_p4) || 0;
        totals.totalDayaTampung += Number(item.daya_tampung) || 0;
      })
    : [];

  const totalPvalid =
    Number(totals.totalValidP1) +
    Number(totals.totalValidP2) +
    Number(totals.totalValidP3) +
    Number(totals.totalValidP4);
  const totalP =
    Number(totals.totalP1) +
    Number(totals.totalP2) +
    Number(totals.totalP3) +
    Number(totals.totalP4);

  const isFilterActive = !!idsJalurJenisParams;

  if (isPendingDataRekapPendaftaran) return <SkeletonDashboard />;

  // Helper Card Component
  const StatCard = ({ title, total, valid, colorClass, icon }: any) => (
    <Card className={`border-none shadow-sm dark:bg-slate-900 ${colorClass}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            {title}
          </p>
          <div className="flex items-baseline gap-2">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              {valid}
            </h2>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Valid
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Total Pendaftar: <span className="font-semibold">{total}</span>
          </p>
        </div>
        <div className="p-3 bg-white/50 dark:bg-slate-800/50 rounded-full text-slate-700 dark:text-slate-300">
          {icon}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* 1. METRIC CARDS SECTION */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Keseluruhan"
          valid={dataRekapPendaftaran?.total?.pendaftar_valid_p1 || 0}
          total={dataRekapPendaftaran?.total?.pendaftar_p1 || 0}
          colorClass="bg-blue-50 dark:bg-blue-950/20"
          icon={
            <FaUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          }
        />
        <StatCard
          title="SMBJM - CBT"
          valid={
            dataRekapPendaftaran?.total_perjalur["SMBJM-CBT"]
              ?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur["SMBJM-CBT"]?.pendaftar_p1 || 0
          }
          colorClass="bg-emerald-50 dark:bg-emerald-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          }
        />
        <StatCard
          title="SMBJM - Nilai Rapor"
          valid={
            dataRekapPendaftaran?.total_perjalur["SMBJM-NILAI RAPORT"]
              ?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur["SMBJM-NILAI RAPORT"]
              ?.pendaftar_p1 || 0
          }
          colorClass="bg-purple-50 dark:bg-purple-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          }
        />
        <StatCard
          title="Talent Scouting"
          valid={
            dataRekapPendaftaran?.total_perjalur["SMBJM-TALENT SCOUTING"]
              ?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur["SMBJM-TALENT SCOUTING"]
              ?.pendaftar_p1 || 0
          }
          colorClass="bg-indigo-50 dark:bg-indigo-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          }
        />

        <StatCard
          title="Skor UTBK"
          valid={
            dataRekapPendaftaran?.total_perjalur["SMBJM-SKOR UTBK SNBT"]
              ?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur["SMBJM-SKOR UTBK SNBT"]
              ?.pendaftar_p1 || 0
          }
          colorClass="bg-red-50 dark:bg-red-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-red-600 dark:text-red-400" />
          }
        />
        <StatCard
          title="Kepemimpinan"
          valid={
            dataRekapPendaftaran?.total_perjalur["SMBJM-KEPEMIMPINAN"]
              ?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur["SMBJM-KEPEMIMPINAN"]
              ?.pendaftar_p1 || 0
          }
          colorClass="bg-sky-50 dark:bg-sky-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-sky-600 dark:text-sky-400" />
          }
        />
        <StatCard
          title="Sekolah Lab"
          valid={
            dataRekapPendaftaran?.total_perjalur[
              "SMBJM-LULUSAN SMAS LAB UNDIKSHA"
            ]?.pendaftar_valid_p1 || 0
          }
          total={
            dataRekapPendaftaran?.total_perjalur[
              "SMBJM-LULUSAN SMAS LAB UNDIKSHA"
            ]?.pendaftar_p1 || 0
          }
          colorClass="bg-pink-50 dark:bg-pink-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-pink-600 dark:text-pink-400" />
          }
        />
      </section>

      {/* 2. HEADER & FILTER SECTION */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Rekap Penerimaan
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Monitoring data pendaftar per prodi dan jalur.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Input
            placeholder="Cari program studi..."
            className="sm:w-[250px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant={isFilterActive ? "default" : "outline"}
              className={`flex items-center gap-2 ${
                isFilterActive
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "dark:bg-slate-900 dark:text-slate-200 dark:border-slate-800 dark:hover:bg-slate-800"
              }`}
              onClick={() => setShowFilter((show) => !show)}
            >
              <LuListFilter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </Button>
            <Button
              variant={"outline"}
              className="flex items-center gap-2 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-800 dark:hover:bg-slate-800"
              onClick={() => {
                if (idsJalurJenisParams)
                  searchParams.set("id_ref_jalur_jenis", idsJalurJenisParams);
                window.open(
                  `${apiBaseUrl}/dashboard/rekap-pendaftaran-smbjm/export?${searchParams?.toString()}`,
                );
              }}
            >
              <LuDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>
      </div>

      {/* 3. TABLE SECTION */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
        <div className="overflow-x-auto h-[65vh] relative">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 sticky top-0 z-20 shadow-sm uppercase font-semibold">
              <tr>
                <th
                  className="py-3 px-4 border-b dark:border-slate-800 sticky left-0 z-30 bg-slate-50 dark:bg-slate-900 w-[200px]"
                  rowSpan={2}
                >
                  Jalur & Prodi
                </th>
                <th
                  className="py-3 px-4 border-b dark:border-slate-800 text-center"
                  rowSpan={2}
                >
                  Kode
                </th>
                <th
                  className="py-3 px-4 border-b dark:border-slate-800 text-start"
                  rowSpan={2}
                >
                  Fakultas
                </th>
                <th
                  className="py-3 px-4 border-b dark:border-slate-800 text-center"
                  rowSpan={2}
                >
                  Daya Tampung
                </th>
                <th
                  className="py-2 px-4 border-b dark:border-slate-800 text-center bg-blue-50/50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400"
                  colSpan={5}
                >
                  Pendaftar Valid
                </th>
                <th
                  className="py-2 px-4 border-b dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300"
                  colSpan={5}
                >
                  Total Pendaftar
                </th>
              </tr>
              <tr>
                {/* VALID COLUMNS HEADER */}
                {Array(4)
                  .fill(null)
                  .map((_, index) => (
                    <th
                      key={`valid-${index}`}
                      className="py-2 px-4 border-b dark:border-slate-800 bg-blue-50/30 dark:bg-blue-900/5"
                    >
                      <div
                        className="flex items-center justify-center gap-1 cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => {
                          if (sortIndex === index && sortKeyMode === "valid") {
                            setSortAsc(!sortAsc);
                          } else {
                            setSortIndex(index);
                            setSortKeyMode("valid");
                            setSortAsc(true);
                          }
                        }}
                      >
                        <span>P{index + 1}</span>
                        <FaSort className="w-3 h-3 opacity-50" />
                      </div>
                    </th>
                  ))}
                <th
                  className="py-2 px-4 border-b dark:border-slate-800 bg-blue-100/30 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-bold text-center cursor-pointer"
                  onClick={() => {
                    if (sortKeyMode === "grand-valid") {
                      setSortAsc(!sortAsc);
                    } else {
                      setSortKeyMode("grand-valid");
                      setSortIndex(null);
                      setSortAsc(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-center gap-1">
                    TOTAL <FaSort className="w-3 h-3 opacity-50" />
                  </div>
                </th>

                {/* RAW COLUMNS HEADER */}
                {Array(4)
                  .fill(null)
                  .map((_, index) => (
                    <th
                      key={`raw-${index}`}
                      className="py-2 px-4 border-b dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30"
                    >
                      <div
                        className="flex items-center justify-center gap-1 cursor-pointer hover:text-slate-900 transition-colors"
                        onClick={() => {
                          if (sortIndex === index && sortKeyMode === "raw") {
                            setSortAsc(!sortAsc);
                          } else {
                            setSortIndex(index);
                            setSortKeyMode("raw");
                            setSortAsc(true);
                          }
                        }}
                      >
                        <span>P{index + 1}</span>
                        <FaSort className="w-3 h-3 opacity-50" />
                      </div>
                    </th>
                  ))}
                <th
                  className="py-2 px-4 border-b dark:border-slate-800 bg-slate-100/50 dark:bg-slate-800/50 font-bold text-center cursor-pointer"
                  onClick={() => {
                    if (sortKeyMode === "grand-raw") {
                      setSortAsc(!sortAsc);
                    } else {
                      setSortKeyMode("grand-raw");
                      setSortIndex(null);
                      setSortAsc(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-center gap-1">
                    TOTAL <FaSort className="w-3 h-3 opacity-50" />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
              {sortedFiltered?.length > 0 ? (
                sortedFiltered?.map(
                  (item: DataRekapPendaftaran, index: number) => {
                    const totalPendaftarValid = [
                      "pendaftar_valid_p1",
                      "pendaftar_valid_p2",
                      "pendaftar_valid_p3",
                      "pendaftar_valid_p4",
                    ].reduce(
                      (sum, key) =>
                        sum +
                        (Number(item[key as keyof DataRekapPendaftaran]) || 0),
                      0,
                    );

                    const totalPendaftar = [
                      "pendaftar_p1",
                      "pendaftar_p2",
                      "pendaftar_p3",
                      "pendaftar_p4",
                    ].reduce(
                      (sum, key) =>
                        sum +
                        (Number(item[key as keyof DataRekapPendaftaran]) || 0),
                      0,
                    );

                    return (
                      <tr
                        key={index}
                        className="hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                      >
                        <td className="py-3 px-4 sticky left-0 bg-white dark:bg-slate-950 border-r dark:border-slate-800 z-10">
                          <div className="flex flex-col">
                            <span
                              className="font-medium text-slate-900 dark:text-slate-200 text-sm"
                              title={item?.nama}
                            >
                              {item?.nama}
                            </span>
                            <span className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wide">
                              {item?.nama_jalur_jenis}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center font-mono text-xs">
                          {item?.uk_kode}
                        </td>
                        <td className="py-3 px-4 text-start text-xs">
                          {item?.fakultas ?? "-"}
                        </td>
                        <td className="py-3 px-4 text-center font-medium">
                          {item?.daya_tampung}
                        </td>

                        {/* VALID DATA CELLS */}
                        {[
                          "pendaftar_valid_p1",
                          "pendaftar_valid_p2",
                          "pendaftar_valid_p3",
                          "pendaftar_valid_p4",
                        ].map((key) => (
                          <td
                            key={key}
                            className="py-3 px-4 text-center bg-blue-50/20 dark:bg-blue-900/5"
                          >
                            {Number(item[key as keyof DataRekapPendaftaran]) >
                            0 ? (
                              <span className="font-medium text-blue-700 dark:text-blue-400">
                                {item[key as keyof DataRekapPendaftaran]}
                              </span>
                            ) : (
                              <span className="text-slate-300 dark:text-slate-700">
                                -
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="py-3 px-4 text-center font-bold bg-blue-100/30 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300">
                          {totalPendaftarValid}
                        </td>

                        {/* RAW DATA CELLS */}
                        {[
                          "pendaftar_p1",
                          "pendaftar_p2",
                          "pendaftar_p3",
                          "pendaftar_p4",
                        ].map((key) => (
                          <td
                            key={key}
                            className="py-3 px-4 text-center text-slate-500 dark:text-slate-500"
                          >
                            {Number(item[key as keyof DataRekapPendaftaran]) > 0
                              ? item[key as keyof DataRekapPendaftaran]
                              : "-"}
                          </td>
                        ))}
                        <td className="py-3 px-4 text-center font-bold bg-slate-100/50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100">
                          {totalPendaftar}
                        </td>
                      </tr>
                    );
                  },
                )
              ) : (
                <tr>
                  <td
                    className="py-8 px-4 text-center text-slate-500 dark:text-slate-400"
                    colSpan={14}
                  >
                    Data tidak ditemukan.
                  </td>
                </tr>
              )}
            </tbody>

            <tfoot className="sticky bottom-0 bg-slate-100 dark:bg-slate-900 font-bold text-slate-900 dark:text-slate-100 shadow-inner z-20">
              <tr>
                <td
                  className="py-3 px-4 sticky left-0 bg-slate-100 dark:bg-slate-900 border-r dark:border-slate-800 text-center"
                  colSpan={3}
                >
                  TOTAL KESELURUHAN
                </td>
                <td className="py-3 px-4 text-center">
                  {totals?.totalDayaTampung}
                </td>
                <td className="py-3 px-4 text-center text-blue-700 dark:text-blue-400">
                  {totals?.totalValidP1}
                </td>
                <td className="py-3 px-4 text-center text-blue-700 dark:text-blue-400">
                  {totals?.totalValidP2}
                </td>
                <td className="py-3 px-4 text-center text-blue-700 dark:text-blue-400">
                  {totals?.totalValidP3}
                </td>
                <td className="py-3 px-4 text-center text-blue-700 dark:text-blue-400">
                  {totals?.totalValidP4}
                </td>
                <td className="py-3 px-4 text-center bg-blue-200/50 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200">
                  {totalPvalid}
                </td>

                <td className="py-3 px-4 text-center">{totals?.totalP1}</td>
                <td className="py-3 px-4 text-center">{totals?.totalP2}</td>
                <td className="py-3 px-4 text-center">{totals?.totalP3}</td>
                <td className="py-3 px-4 text-center">{totals?.totalP4}</td>
                <td className="py-3 px-4 text-center bg-slate-200/50 dark:bg-slate-800/60">
                  {totalP}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilterPenerimaan closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  );
}
